<template>
	<dms-wrapper ref="wrapper" :label="labelText" :show-label="!isHiddenLabel && showLabel" v-bind="{span, wrapp, required, validationRules}"
		:validation-property="validationPropertyCmp" :disabled="isDisabled">
		<div class="editor">
			<editor-menu-bar v-slot="{ commands, isActive }" :editor="editor">
				<div class="menubar">
					<div class="toolbar">
						<dms-rich-text-button name="remove" @onClick="clearFormatting" />

						<dms-rich-text-button name="undo" @onClick="commands.undo" />

						<dms-rich-text-button name="redo" @onClick="commands.redo" />

						<dms-rich-text-button :is-active="isActive.bold()" name="bold" @onClick="commands.bold" />

						<dms-rich-text-button :is-active="isActive.italic()" name="italic" @onClick="commands.italic" />

						<dms-rich-text-button :is-active="isActive.strike()" name="strike" @onClick="commands.strike" />

						<dms-rich-text-button :is-active="isActive.underline()" name="underline" @onClick="commands.underline" />

						<dms-rich-text-button :is-active="isActive.bullet_list()" name="ul" @onClick="commands.bullet_list" />

						<dms-rich-text-button :is-active="isActive.ordered_list()" name="ol" @onClick="commands.ordered_list" />

						<dms-rich-text-button :is-image="false" :is-active="isActive.heading({ level: 1 })" name="H1" @onClick="commands.heading({ level: 1 })" />

						<dms-rich-text-button :is-image="false" :is-active="isActive.heading({ level: 2 })" name="H2" @onClick="commands.heading({ level: 2 })" />

						<dms-rich-text-button :is-image="false" :is-active="isActive.heading({ level: 3 })" name="H3" @onClick="commands.heading({ level: 3 })" />

						<dms-rich-text-button name="table" @onClick="commands.createTable({rowsCount: 3, colsCount: 3, withHeaderRow: false })" />

						<slot name="toolbarButtons" :commands="commands" :is-active="isActive" />
					</div>

					<div v-if="isActive.table()" class="toolbar">
						<dms-rich-text-button name="delete_table" @onClick="commands.deleteTable" />
						<dms-rich-text-button name="add_col_before" @onClick="commands.addColumnBefore" />
						<dms-rich-text-button name="add_col_after" @onClick="commands.addColumnAfter" />
						<dms-rich-text-button name="delete_col" @onClick="commands.deleteColumn" />
						<dms-rich-text-button name="add_row_before" @onClick="commands.addRowBefore" />
						<dms-rich-text-button name="add_row_after" @onClick="commands.addRowAfter" />
						<dms-rich-text-button name="delete_row" @onClick="commands.deleteRow" />
						<dms-rich-text-button name="combine_cells" @onClick="commands.toggleCellMerge" />
					</div>
				</div>
			</editor-menu-bar>
			<editor-content ref="editor" :editor="editor" class="editor__content" />
		</div>
	</dms-wrapper>
</template>

<script>
import { Editor, EditorContent, EditorMenuBar } from 'tiptap';
import {
	Bold,
	Italic,
	Strike,
	Underline,
	Table,
	TableHeader,
	TableCell,
	TableRow,
	History,
	HardBreak,
	Heading,
	BulletList,
	OrderedList,
	ListItem,
	Link,
} from 'tiptap-extensions';

import { BaseInput } from '#components/dms/common/baseInput';
import DmsWrapper from '#components/dms/form/Wrapper.vue';

import DmsRichTextButton from './RichTextButton.vue';

export default BaseInput.extend({
	name: 'DmsRichText',
	components: {
		DmsRichTextButton,
		EditorMenuBar,
		EditorContent,
		DmsWrapper,
	},
	props: {
		value: {
			validator: (prop) => typeof prop === 'string' || prop === null,
			required: true,
		},
		required: { type: Boolean, default: undefined },
		span: { type: Number, default: 24 },
		readonly: { type: Boolean, default: false },
	},
	data() {
		return {
			editor: new Editor({
				editable: !this.readonly,
				extensions: [
					new Link({ openOnClick: false }),
					new HardBreak(),
					new Heading({ levels: [1, 2, 3] }),
					new Bold(),
					new Italic(),
					new Strike(),
					new Underline(),
					new Table({
						resizable: true,
					}),
					new TableHeader(),
					new TableCell(),
					new TableRow(),
					new History(),
					new ListItem(),
					new BulletList(),
					new OrderedList(),
				],
				content: this.value,
			}),
		};
	},
	watch: {
		isDisabled() {
			this.setDisabledAndReadOnly();
		},
		value(val) {
			if (val !== this.editor.getHTML()) {
				this.editor.setContent(val);
			}
		},
	},
	created() {
		this.editor.on('update', ({ getHTML }) => {
			this.$emit('update:value', getHTML());
		});
	},
	beforeDestroy() {
		this.editor.destroy();
	},
	mounted() {
		this.$refs.wrapper && (this.formItem = this.$refs.wrapper.formItem);
		this.setDisabledAndReadOnly();
	},
	methods: {
		setDisabledAndReadOnly() {
			this.editor.setOptions({
				editable: !this.isDisabled && !this.readonly,
			});
		},
		clearFormatting() {
			const newValue = this.value.replace(/<[^>]+>/g, '');
			this.editor.setContent(newValue, true);
		},
		getRawText() {
			return this.value.replace(/<[^>]+>/g, '');
		},
		setLink() {
			const previousUrl = this.editor.getAttributes('link').href;

			const url = this.prompt('URL', true, previousUrl);
			const extendRange = () => this.editor
				.chain()
				.focus()
				.extendMarkRange('link');

			if (url === '') {
				extendRange().unsetLink().run();
				return;
			}

			extendRange().setLink({ href: url }).run();
		},
		setUrl(command) {
			const { state } = this.editor;
			const { from, to } = state.selection;
			// Marks are used to add extra styling or other information to inline content like a strong tag or links
			let highlightedControls = [];
			state.doc.nodesBetween(from, to, (node) => {
				highlightedControls = [...highlightedCOntrols, ...node.marks];
			});
			const markedControlForHighlight = highlightedControls.find((markedItem) => markedItem.type.name === 'link');
			let urlSetting = '';
			if (markedControlForHighlight && markedControlForHighlight.attrs.href) {
				const presetURL = markedControlForHighlight.attrs.href;
				urlSetting = this.prompt(`${this.LocalizeChyboveHlasky('DMS_UPDATEURL')}`, true, presetURL); // let a user see the previously set URL
			} else {
				urlSetting = this.prompt(this.LocalizeChyboveHlasky('DMS_ADDURL'), true, ''); // a clean prompt, has had no anchor
			}
			if (urlSetting === null) {
				return;
			}
			command({ href: urlSetting });
		},
	},
});
</script>
